import { CityData, CityBounds, LatLng, POI, POICategory, TransitLine, TransitStop, ZoneType, NavEdge } from '../types/map';
import { RawOSMData, OSMElement } from './overpass';
import { buildNavigationGraph } from './graphBuilder';
import { classifyZones, classifyNeighborhoods } from './zoneClassifier';
import { dedupeStopsOnLines } from '../engine/transit';

/**
 * Point-in-polygon test (ray casting algorithm).
 * Polygon is an array of [lng, lat] coordinate pairs forming a closed ring.
 */
export function pointInPolygon(lng: number, lat: number, ring: [number, number][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0], yi = ring[i][1];
    const xj = ring[j][0], yj = ring[j][1];
    if (((yi > lat) !== (yj > lat)) && (lng < (xj - xi) * (lat - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  return inside;
}

/**
 * Build a lookup of zone polygons from the classified zones GeoJSON.
 */
export function buildZoneLookup(zones: GeoJSON.FeatureCollection): { ring: [number, number][]; zoneType: ZoneType }[] {
  const zonePolygons: { ring: [number, number][]; zoneType: ZoneType }[] = [];
  for (const feature of zones.features) {
    if (feature.geometry.type !== 'Polygon') continue;
    const zoneType = feature.properties?.zoneType as ZoneType | undefined;
    if (!zoneType) continue;
    const ring = feature.geometry.coordinates[0] as [number, number][];
    if (ring && ring.length >= 3) {
      zonePolygons.push({ ring, zoneType });
    }
  }
  return zonePolygons;
}

/**
 * Find which zone a point falls inside. Returns 'residential' if no match.
 */
/**
 * Zone priority order — higher-priority zones win when a point is
 * inside multiple overlapping zone polygons (e.g. commercial overrides residential).
 */
const ZONE_PRIORITY: Record<ZoneType, number> = {
  commercial: 6,
  transit: 5,
  'mixed-use': 4,
  civic: 3,
  industrial: 2,
  park: 1,
  residential: 0,
};

export function zoneAtPoint(
  lng: number,
  lat: number,
  zonePolygons: { ring: [number, number][]; zoneType: ZoneType }[],
): ZoneType {
  let bestZone: ZoneType | null = null;
  let bestPriority = -1;

  for (const { ring, zoneType } of zonePolygons) {
    if (pointInPolygon(lng, lat, ring)) {
      const priority = ZONE_PRIORITY[zoneType] ?? 0;
      if (priority > bestPriority) {
        bestPriority = priority;
        bestZone = zoneType;
      }
    }
  }

  return bestZone ?? 'residential';
}

/**
 * Assign zone types to navigation edges based on which zone polygon
 * the edge's midpoint falls inside. Edges outside any zone polygon
 * keep their default 'residential' zone.
 */
function assignZonesToEdges(
  edges: NavEdge[],
  zonePolygons: { ring: [number, number][]; zoneType: ZoneType }[],
): void {
  for (const edge of edges) {
    const geom = edge.geometry;
    const midIdx = Math.floor(geom.length / 2);
    edge.zone = zoneAtPoint(geom[midIdx][0], geom[midIdx][1], zonePolygons);
  }
}

/**
 * Assign zone types to street GeoJSON features so the hover handler
 * can read zone info directly from the feature properties.
 */
function assignZonesToStreets(
  streets: GeoJSON.FeatureCollection,
  zonePolygons: { ring: [number, number][]; zoneType: ZoneType }[],
): void {
  for (const feature of streets.features) {
    if (feature.geometry.type !== 'LineString') continue;
    const coords = feature.geometry.coordinates as [number, number][];
    const midIdx = Math.floor(coords.length / 2);
    const zone = zoneAtPoint(coords[midIdx][0], coords[midIdx][1], zonePolygons);
    feature.properties = feature.properties || {};
    feature.properties.zone = zone;
  }
}

function buildStreetGeoJSON(streets: OSMElement[]): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];

  for (const way of streets) {
    if (!way.geometry || way.geometry.length < 2) continue;
    const tags = way.tags || {};
    const highway = tags.highway || '';

    let tier: string;
    if (['motorway', 'trunk', 'primary', 'secondary'].includes(highway)) tier = 'major';
    else if (highway === 'tertiary') tier = 'minor';
    else if (['residential', 'unclassified', 'living_street', 'service'].includes(highway)) tier = 'residential';
    else tier = 'footpath';

    features.push({
      type: 'Feature',
      properties: {
        id: `street-${way.id}`,
        highway,
        tier,
        name: tags.name || '',
        bridge: tags.bridge === 'yes',
        tunnel: tags.tunnel === 'yes',
      },
      geometry: {
        type: 'LineString',
        coordinates: way.geometry.map(p => [p.lon, p.lat]),
      },
    });
  }

  // Simplify footpaths: merge connected segments into longer polylines
  // to reduce visual clutter from hundreds of tiny node-to-node segments
  const nonFootpath = features.filter(f => f.properties?.tier !== 'footpath');
  const footpaths = features.filter(f => f.properties?.tier === 'footpath');

  if (footpaths.length > 0) {
    // Build adjacency: endpoint coord string → list of footpath features touching that point
    const endpointMap = new Map<string, { feature: GeoJSON.Feature; coordIdx: 'start' | 'end' }[]>();
    const coordKey = (c: number[]) => `${c[0].toFixed(6)},${c[1].toFixed(6)}`;

    for (const fp of footpaths) {
      const coords = (fp.geometry as GeoJSON.LineString).coordinates;
      if (coords.length < 2) continue;
      const startKey = coordKey(coords[0]);
      const endKey = coordKey(coords[coords.length - 1]);
      if (!endpointMap.has(startKey)) endpointMap.set(startKey, []);
      endpointMap.get(startKey)!.push({ feature: fp, coordIdx: 'start' });
      if (startKey !== endKey) {
        if (!endpointMap.has(endKey)) endpointMap.set(endKey, []);
        endpointMap.get(endKey)!.push({ feature: fp, coordIdx: 'end' });
      }
    }

    // Greedy chain merge: walk along connected footpaths
    const used = new Set<GeoJSON.Feature>();
    const merged: GeoJSON.Feature[] = [];

    for (const fp of footpaths) {
      if (used.has(fp)) continue;
      used.add(fp);
      let chain = [...(fp.geometry as GeoJSON.LineString).coordinates];

      // Extend forward from chain end
      let extending = true;
      while (extending) {
        extending = false;
        const tailKey = coordKey(chain[chain.length - 1]);
        const neighbors = endpointMap.get(tailKey);
        if (neighbors) {
          for (const n of neighbors) {
            if (used.has(n.feature)) continue;
            const nCoords = (n.feature.geometry as GeoJSON.LineString).coordinates;
            if (nCoords.length < 2) continue;
            used.add(n.feature);
            if (n.coordIdx === 'start') {
              chain.push(...nCoords.slice(1));
            } else {
              chain.push(...[...nCoords].reverse().slice(1));
            }
            extending = true;
            break;
          }
        }
      }

      // Extend backward from chain start
      extending = true;
      while (extending) {
        extending = false;
        const headKey = coordKey(chain[0]);
        const neighbors = endpointMap.get(headKey);
        if (neighbors) {
          for (const n of neighbors) {
            if (used.has(n.feature)) continue;
            const nCoords = (n.feature.geometry as GeoJSON.LineString).coordinates;
            if (nCoords.length < 2) continue;
            used.add(n.feature);
            if (n.coordIdx === 'end') {
              chain = [...nCoords.slice(0, -1), ...chain];
            } else {
              chain = [...[...nCoords].reverse().slice(0, -1), ...chain];
            }
            extending = true;
            break;
          }
        }
      }

      merged.push({
        type: 'Feature',
        properties: { tier: 'footpath', name: '', highway: 'footway' },
        geometry: { type: 'LineString', coordinates: chain },
      });
    }

    return { type: 'FeatureCollection', features: [...nonFootpath, ...merged] };
  }

  return { type: 'FeatureCollection', features };
}

function buildBuildingGeoJSON(buildings: OSMElement[]): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];

  for (const way of buildings) {
    if (!way.geometry || way.geometry.length < 3) continue;
    const tags = way.tags || {};
    const coords: [number, number][] = way.geometry.map(p => [p.lon, p.lat]);

    // Close ring
    const first = coords[0];
    const last = coords[coords.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) {
      coords.push([...first] as [number, number]);
    }

    features.push({
      type: 'Feature',
      properties: {
        id: `building-${way.id}`,
        type: tags.building || 'yes',
        name: tags.name || '',
        levels: tags['building:levels'] ? parseInt(tags['building:levels'], 10) : undefined,
        height: tags.height ? parseFloat(tags.height) : undefined,
      },
      geometry: {
        type: 'Polygon',
        coordinates: [coords],
      },
    });
  }

  return { type: 'FeatureCollection', features };
}

function buildWaterGeoJSON(water: OSMElement[]): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];

  for (const element of water) {
    if (!element.geometry || element.geometry.length < 2) continue;
    const tags = element.tags || {};

    const coords: [number, number][] = element.geometry.map(p => [p.lon, p.lat]);

    // Determine if this should be a polygon (closed water body) or line (river/stream)
    const isArea = tags.natural === 'water' || tags.waterway === 'riverbank';
    const first = coords[0];
    const last = coords[coords.length - 1];
    const isClosed = first[0] === last[0] && first[1] === last[1];

    if (isArea && coords.length >= 3) {
      if (!isClosed) coords.push([...first] as [number, number]);
      features.push({
        type: 'Feature',
        properties: { id: `water-${element.id}`, type: tags.natural || tags.waterway || '' },
        geometry: { type: 'Polygon', coordinates: [coords] },
      });
    } else {
      features.push({
        type: 'Feature',
        properties: { id: `water-${element.id}`, type: tags.waterway || tags.natural || '' },
        geometry: { type: 'LineString', coordinates: coords },
      });
    }

    // Handle relations (multipolygon water bodies)
    if (element.type === 'relation' && element.members) {
      for (const member of element.members) {
        if (member.geometry && member.geometry.length >= 3) {
          const memberCoords: [number, number][] = member.geometry.map(p => [p.lon, p.lat]);
          const mFirst = memberCoords[0];
          const mLast = memberCoords[memberCoords.length - 1];
          if (mFirst[0] !== mLast[0] || mFirst[1] !== mLast[1]) {
            memberCoords.push([...mFirst] as [number, number]);
          }
          features.push({
            type: 'Feature',
            properties: { id: `water-${element.id}-${member.ref}`, type: 'water' },
            geometry: { type: 'Polygon', coordinates: [memberCoords] },
          });
        }
      }
    }
  }

  return { type: 'FeatureCollection', features };
}

function categorizePOI(tags: Record<string, string>): POICategory | null {
  const amenity = tags.amenity;
  const shop = tags.shop;
  const tourism = tags.tourism;
  const historic = tags.historic;
  const leisure = tags.leisure;
  const craft = tags.craft;

  if (amenity === 'pharmacy' || amenity === 'hospital' || amenity === 'clinic') return 'medical';
  if (shop === 'hardware' || shop === 'doityourself' || craft === 'locksmith') return 'hardware';
  if (shop === 'sports' || shop === 'outdoor' || shop === 'hunting') return 'sporting';
  if (shop === 'electronics' || shop === 'mobile_phone') return 'electronics';
  if (shop === 'military_surplus' || shop === 'weapons') return 'military';
  if (leisure === 'park' || amenity === 'bench') return 'park';
  if (amenity === 'restaurant' || amenity === 'cafe' || amenity === 'bar') return 'restaurant';
  if (amenity === 'marketplace' || shop === 'supermarket') return 'market';
  if (tourism === 'attraction' || tourism === 'museum' || historic) return 'landmark';
  if (tourism === 'hotel' || tourism === 'hostel') return 'hotel';
  if (amenity === 'locker') return 'locker';
  return null;
}

function extractPOIs(pois: OSMElement[]): POI[] {
  const results: POI[] = [];

  for (const element of pois) {
    const tags = element.tags || {};
    const category = categorizePOI(tags);
    if (!category) continue;

    let lat = element.lat;
    let lng = element.lon;
    // For ways, compute centroid from geometry
    if ((lat === undefined || lng === undefined) && element.geometry && element.geometry.length > 0) {
      let sumLat = 0, sumLng = 0, count = 0;
      for (const pt of element.geometry) {
        if (pt.lat !== undefined && pt.lon !== undefined) {
          sumLat += pt.lat;
          sumLng += pt.lon;
          count++;
        }
      }
      if (count > 0) {
        lat = sumLat / count;
        lng = sumLng / count;
      }
    }
    // For relations, compute centroid from member geometries
    if ((lat === undefined || lng === undefined) && element.members && element.members.length > 0) {
      let sumLat = 0, sumLng = 0, count = 0;
      for (const member of element.members) {
        if (member.geometry) {
          for (const pt of member.geometry) {
            if (pt.lat !== undefined && pt.lon !== undefined) {
              sumLat += pt.lat;
              sumLng += pt.lon;
              count++;
            }
          }
        } else if (member.lat !== undefined && member.lon !== undefined) {
          sumLat += member.lat;
          sumLng += member.lon;
          count++;
        }
      }
      if (count > 0) {
        lat = sumLat / count;
        lng = sumLng / count;
      }
    }
    if (lat === undefined || lng === undefined) continue;

    results.push({
      id: `poi-${element.id}`,
      name: tags.name || tags.amenity || tags.shop || tags.tourism || 'Unknown',
      lat,
      lng,
      category,
      osmTags: tags,
    });
  }

  return results;
}

const TRANSIT_COLORS = [
  '#ff4444', '#44aaff', '#ffaa44', '#44ff88', '#ff44aa',
  '#88aaff', '#ffdd44', '#44ffcc', '#ff8844', '#aa44ff',
];

function extractTransit(transit: OSMElement[]): { lines: TransitLine[]; stops: TransitStop[] } {
  const lines: TransitLine[] = [];
  const stopMap = new Map<string, TransitStop>();
  let colorIndex = 0;

  // Build a spatial lookup of named station nodes so we can match them to unnamed stop positions
  const namedStations: { id: number; name: string; lat: number; lon: number }[] = [];
  for (const element of transit) {
    if (element.type === 'node' && element.tags?.name && element.lat && element.lon) {
      namedStations.push({ id: element.id, name: element.tags.name, lat: element.lat, lon: element.lon });
    }
  }

  // Find nearest named station within ~200m of a given point
  function findNearestStationName(lat: number, lon: number): string {
    const THRESHOLD = 0.002; // ~200m in degrees
    let bestName = '';
    let bestDist = Infinity;
    for (const s of namedStations) {
      const dlat = s.lat - lat;
      const dlon = s.lon - lon;
      const dist = dlat * dlat + dlon * dlon;
      if (dist < bestDist && dist < THRESHOLD * THRESHOLD) {
        bestDist = dist;
        bestName = s.name;
      }
    }
    return bestName;
  }

  // Extract stations from standalone nodes
  for (const element of transit) {
    if (element.type !== 'node') continue;
    const tags = element.tags || {};
    if (!element.lat || !element.lon) continue;

    const stopId = `stop-${element.id}`;
    if (!stopMap.has(stopId)) {
      stopMap.set(stopId, {
        id: stopId,
        name: tags.name || 'Station',
        lat: element.lat,
        lng: element.lon,
        lines: [],
      });
    }
  }

  // Extract route relations
  for (const element of transit) {
    if (element.type !== 'relation') continue;
    const tags = element.tags || {};
    if (!element.members) continue;

    const routeType = tags.route as 'subway' | 'tram' | 'light_rail' | 'bus' | 'ferry';
    const lineType = routeType === 'light_rail' ? 'tram' : (routeType || 'subway');
    let lineColor = tags.colour || tags.color || TRANSIT_COLORS[colorIndex % TRANSIT_COLORS.length];
    // Lighten very dark transit colors so they're visible on the dark map
    if (lineColor && lineColor.startsWith('#') && lineColor.length >= 7) {
      const r = parseInt(lineColor.slice(1, 3), 16);
      const g = parseInt(lineColor.slice(3, 5), 16);
      const b = parseInt(lineColor.slice(5, 7), 16);
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      if (brightness < 80) {
        // Boost toward a lighter version
        const boost = Math.max(80, Math.round(brightness * 2.5));
        const nr = Math.min(255, r + boost);
        const ng = Math.min(255, g + boost);
        const nb = Math.min(255, b + boost);
        lineColor = `#${nr.toString(16).padStart(2, '0')}${ng.toString(16).padStart(2, '0')}${nb.toString(16).padStart(2, '0')}`;
      }
    }
    colorIndex++;

    const lineId = `line-${element.id}`;
    const lineStops: TransitStop[] = [];
    const lineGeometry: [number, number][][] = [];

    // Collect stops from relation members
    for (const member of element.members) {
      if (member.type === 'node' && (member.role === 'stop' || member.role === 'platform' || member.role === '')) {
        if (member.lat && member.lon) {
          const stopId = `stop-${member.ref}`;
          if (!stopMap.has(stopId)) {
            stopMap.set(stopId, {
              id: stopId,
              name: findNearestStationName(member.lat!, member.lon!),
              lat: member.lat!,
              lng: member.lon!,
              lines: [lineId],
            });
          }
          const stop = stopMap.get(stopId)!;
          if (!stop.lines.includes(lineId)) stop.lines.push(lineId);
          lineStops.push(stop);
        }
      }

      // Collect route geometry from way members
      if (member.type === 'way' && member.geometry && member.geometry.length >= 2) {
        lineGeometry.push(member.geometry.map(p => [p.lon, p.lat]));
      }
    }

    if (lineStops.length > 0 || lineGeometry.length > 0) {
      lines.push({
        id: lineId,
        name: tags.name || tags.ref || `Line ${lines.length + 1}`,
        type: lineType as 'subway' | 'tram' | 'bus' | 'ferry',
        color: lineColor,
        stops: lineStops,
        geometry: lineGeometry,
      });
    }
  }

  return { lines, stops: Array.from(stopMap.values()) };
}

export function processCityData(
  raw: RawOSMData,
  cityName: string,
  center: LatLng,
  bounds: CityBounds
): CityData {
  const streets = buildStreetGeoJSON(raw.streets);
  const buildings = buildBuildingGeoJSON(raw.buildings);
  const water = buildWaterGeoJSON(raw.water);
  const zones = classifyZones(raw.landuse);
  const neighborhoods = classifyNeighborhoods(raw.neighborhoods);
  const navigation = buildNavigationGraph(raw.streets);
  const zonePolygons = buildZoneLookup(zones);
  assignZonesToEdges(navigation.edges, zonePolygons);
  assignZonesToStreets(streets, zonePolygons);
  const pois = extractPOIs(raw.pois);
  const transit = extractTransit(raw.transit);
  // Collapse duplicate/near-duplicate stops (raw OSM often has multiple points per station)
  transit.lines = dedupeStopsOnLines(transit.lines);

  return {
    name: cityName,
    bounds,
    center,
    layers: {
      streets,
      buildings,
      water,
      transit,
      zones,
      neighborhoods,
    },
    navigation,
    pois,
  };
}
